// Thin fetch wrapper around the Toffs Cloud backend.
//
// The backend wraps success responses as { data, meta } and errors as
// { error: { code, message } }. `request` unwraps `data` and throws an Error
// carrying the API message on failure.

const API_BASE =
  process.env.REACT_APP_API_BASE || 'http://localhost:8080/api/v1';

// ---- Auth token storage -------------------------------------------------
// The bearer token is persisted in localStorage and attached to every request.
const TOKEN_KEY = 'toffs.token';

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

// onUnauthorized lets the auth layer react (e.g. drop the session, redirect to
// login) when the API rejects a token. Registered by AuthProvider.
let onUnauthorized = null;
export function setUnauthorizedHandler(fn) {
  onUnauthorized = fn;
}

// Most callers only want the payload, so `request` unwraps `data`. Callers that
// need the envelope's `meta` (totals, last-synced timestamp) use
// `requestEnvelope` and get { data, meta } as sent.
async function requestEnvelope(path, options = {}) {
  const body = await request(path, { ...options, __envelope: true });
  return body;
}

async function request(path, options = {}) {
  const wantEnvelope = options.__envelope === true;
  delete options.__envelope;
  const token = getToken();
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });

  if (res.status === 204) return null;

  let body = null;
  try {
    body = await res.json();
  } catch {
    body = null;
  }

  if (!res.ok) {
    // A 401 on anything other than the login attempt means the session is
    // gone/expired — clear it and let the app redirect to login.
    if (res.status === 401 && path !== '/auth/login') {
      setToken(null);
      if (onUnauthorized) onUnauthorized();
    }
    const message = body?.error?.message || `Request failed (${res.status})`;
    throw new Error(message);
  }

  if (wantEnvelope) return body ?? { data: null, meta: {} };
  return body?.data ?? body;
}

// Query string for the resource endpoints, dropping empty and null values so an
// unset filter is absent rather than sent as "".
const resourceQuery = (params = {}) => {
  const qs = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v !== '' && v != null)
  ).toString();
  return qs ? `?${qs}` : '';
};

export const api = {
  base: API_BASE,

  // Authentication.
  login: (payload) =>
    request('/auth/login', { method: 'POST', body: JSON.stringify(payload) }),
  // Returns the envelope: { data: user, meta: { features } } — the console areas
  // the account's policies grant.
  getMe: () => requestEnvelope('/auth/me'),

  getOverview: () => request('/overview'),
  getProviders: () => request('/providers'),
  getProvider: (id) => request(`/providers/${id}`),
  // The tenancy's region subscriptions, from the stored snapshot — the only
  // regions a resource can exist in. A database read: nothing here calls the
  // provider, and an empty list means the snapshot has never been synced.
  // getRegionsEnvelope carries meta.home and meta.syncedAt (the snapshot's age).
  getRegions: (id) => request(`/providers/${id}/regions`),
  getRegionsEnvelope: (id) => requestEnvelope(`/providers/${id}/regions`),
  // Refreshes that snapshot from the provider's SDK and stores it (superadmin).
  // The one call that reaches the provider for regions. Resolves to
  // { regions, home, syncedAt }.
  syncRegions: (id) => request(`/providers/${id}/regions/sync`, { method: 'POST' }),

  // The provider's compartment tree, from the stored snapshot — parent before
  // child, each row carrying its depth. This is the set every inventory listing
  // fans out over (one OCI call per compartment), so it is a database read.
  // The envelope carries meta.syncedAt.
  getCompartments: (id) => request(`/providers/${id}/compartments`),
  getCompartmentsEnvelope: (id) =>
    requestEnvelope(`/providers/${id}/compartments`),
  // Refreshes that tree from the provider (superadmin) — the only call that
  // reaches the provider for compartments.
  syncCompartments: (id) =>
    request(`/providers/${id}/compartments/sync`, { method: 'POST' }),
  // Creates a compartment (superadmin). Name and description are both required by
  // OCI; parentId empty means the provider's configured compartment. The new
  // compartment comes back CREATING — it is provisioned asynchronously.
  createCompartment: (id, payload) =>
    request(`/providers/${id}/compartments`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  // Every region the vendor runs, subscribed or not (superadmin) — the choice a
  // subscription is made from. Unfiltered on purpose: diff it against
  // getRegions to get what is left to add.
  getAvailableRegions: (id) => request(`/providers/${id}/available-regions`),
  // Subscribes the tenancy to a region by its key (superadmin). Irreversible —
  // there is no unsubscribe on OCI, so there is no counterpart to this call.
  subscribeRegion: (id, regionKey) =>
    request(`/providers/${id}/regions`, {
      method: 'POST',
      body: JSON.stringify({ regionKey }),
    }),
  getServices: (id) => request(`/providers/${id}/services`),
  // The provider's SKU catalogue: part number → name, unit, category, and its
  // published rates in one currency (volume bands included). Returns the
  // envelope, whose meta carries the currency used and every currency available.
  getProviderSkus: (id, currency = '') =>
    requestEnvelope(
      `/providers/${id}/skus${currency ? `?currency=${encodeURIComponent(currency)}` : ''}`
    ),
  // One compute instance in detail, live from the provider. The id is the
  // provider's own (an OCID) — a live instance is never stored, so this is not
  // the same id space as /resources/{id}.
  getComputeInstance: (providerId, instanceId) =>
    request(
      `/providers/${providerId}/instances/${encodeURIComponent(instanceId)}`
    ),
  // Refreshes that catalogue from the provider's published price list
  // (superadmin). Resolves to { skus, prices, currencies, lastUpdated,
  // syncedAt, syncId, added, changed, removed } — the last three compare this
  // sync's rates against whatever was live immediately before it.
  syncProviderSkus: (id) =>
    request(`/providers/${id}/skus/sync`, { method: 'POST' }),
  // Version history behind the published catalogue: one row per past sync,
  // most recent first. Returns the envelope, meta.count carrying the total.
  getSkuPriceSyncs: (id) => requestEnvelope(`/providers/${id}/sku-price-syncs`),
  // The price changes one sync recorded — every band it added, repriced, or
  // withdrew compared with the version it replaced.
  getSkuPriceSyncChanges: (id, syncId) =>
    requestEnvelope(`/providers/${id}/sku-price-syncs/${syncId}/changes`),
  // Returns the envelope: { data: rows, meta: { fetchedAt, total, ... } }. The
  // caller needs fetchedAt to say how current a same-day figure is.
  getUsageReport: (id, params = {}) => {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== '' && v != null)
    ).toString();
    return requestEnvelope(`/providers/${id}/usage-report${qs ? `?${qs}` : ''}`);
  },
  // Cost per calendar month per service, for the spend trend. Returns the
  // envelope: the rows ({ month, service, currency, cost }) plus meta.axis — every
  // month in the requested span, oldest first — which is the chart's x axis. A
  // month a service reported nothing for has no row at all, so the axis comes from
  // meta rather than from the rows. Summing a month's services gives that month's
  // total, so the total line and the breakdown come from this one call.
  getUsageTrend: (id, params = {}) => {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== '' && v != null)
    ).toString();
    return requestEnvelope(`/providers/${id}/usage-trend${qs ? `?${qs}` : ''}`);
  },
  // The same spend chart per *day*, over a window: { start, end, tenant }. Live from
  // the provider's usage API and superadmin-only, like getUsageTrend and for the same
  // reason — this is the buy side. Rows are ({ date, service, currency, cost }); the
  // month endpoint's rows key on `month`, so a chart reading these has to say which
  // field its bucket is. meta.axis is every day in the window, oldest first, and
  // meta.truncated says the window was longer than the axis is allowed to be.
  //
  // A client's daily chart is NOT this: it is getUsageDays below, which reads the
  // stored snapshot. The two pages never share a source.
  getUsageDaily: (id, params = {}) => {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== '' && v != null)
    ).toString();
    return requestEnvelope(`/providers/${id}/usage-daily${qs ? `?${qs}` : ''}`);
  },
  // Metered data transfer in and out, per calendar month. Its own endpoint rather
  // than a slice of the spend ones: those report what OCI charged, and nothing
  // charges for ingress, so inbound traffic has no cost row to be found in. This one
  // runs a metered-usage query. Returns the envelope: rows ({ month, direction, unit,
  // quantity }) plus meta.axis (the months queried) and meta.units — one chart per
  // unit, since GB and TB share no scale.
  getTransferTrend: (id, params = {}) => {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== '' && v != null)
    ).toString();
    return requestEnvelope(`/providers/${id}/transfer-trend${qs ? `?${qs}` : ''}`);
  },
  getOrganizationTenancies: (id = 'oci', organizationId) =>
    request(
      `/providers/${id}/organization-tenancies${
        organizationId ? `?organizationId=${organizationId}` : ''
      }`
    ),
  // Key Management (KMS) vaults & keys.
  getVaults: (id = 'oci', compartmentId) =>
    request(
      `/providers/${id}/vaults${
        compartmentId ? `?compartmentId=${encodeURIComponent(compartmentId)}` : ''
      }`
    ),
  createVault: (id = 'oci', payload) =>
    request(`/providers/${id}/vaults`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  getVaultKeys: (id = 'oci', vaultId) =>
    request(`/providers/${id}/vaults/${encodeURIComponent(vaultId)}/keys`),
  createVaultKey: (id = 'oci', vaultId, payload) =>
    request(`/providers/${id}/vaults/${encodeURIComponent(vaultId)}/keys`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  vaultEncrypt: (id = 'oci', vaultId, payload) =>
    request(`/providers/${id}/vaults/${encodeURIComponent(vaultId)}/encrypt`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  vaultDecrypt: (id = 'oci', vaultId, payload) =>
    request(`/providers/${id}/vaults/${encodeURIComponent(vaultId)}/decrypt`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  // Resource inventory. `region` names one region, `region: 'all'` covers every
  // region the account is subscribed to (one provider call per region — a
  // provider listing is regional), and omitting it uses the integration's
  // configured region.
  getResources: (params = {}) => request(`/resources${resourceQuery(params)}`),
  // The same read as an envelope: meta.regions says which regions answered and
  // meta.regionErrors which failed. A multi-region read can half-succeed, and a
  // caller drawing the list has to be able to say so rather than showing a short
  // list as if it were the whole inventory.
  getResourcesEnvelope: (params = {}) =>
    requestEnvelope(`/resources${resourceQuery(params)}`),
  createResource: (payload) =>
    request('/resources', { method: 'POST', body: JSON.stringify(payload) }),
  deleteResource: (id) => request(`/resources/${id}`, { method: 'DELETE' }),
  scan: (providerId, region) =>
    request('/scan', {
      method: 'POST',
      body: JSON.stringify({ providerId, region }),
    }),
  getCosts: (providerId) =>
    request(`/costs${providerId ? `?providerId=${providerId}` : ''}`),
  getActivity: (params = {}) => {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== '' && v != null)
    ).toString();
    return request(`/activity${qs ? `?${qs}` : ''}`);
  },

  // Identity & Access Management (superadmin).
  getOrganizations: () => request('/organizations'),
  getOrganization: (id) => request(`/organizations/${id}`),
  createOrganization: (payload) =>
    request('/organizations', { method: 'POST', body: JSON.stringify(payload) }),
  updateOrganization: (id, payload) =>
    request(`/organizations/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),
  deleteOrganization: (id) => request(`/organizations/${id}`, { method: 'DELETE' }),
  getUsers: (params = {}) => {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== '' && v != null)
    ).toString();
    return request(`/users${qs ? `?${qs}` : ''}`);
  },
  createUser: (payload) =>
    request('/users', { method: 'POST', body: JSON.stringify(payload) }),
  updateUser: (id, payload) =>
    request(`/users/${id}`, { method: 'PATCH', body: JSON.stringify(payload) }),
  deleteUser: (id) => request(`/users/${id}`, { method: 'DELETE' }),
  // Superadmin sets or resets a user's password. Returns 204, no body.
  setUserPassword: (id, password) =>
    request(`/users/${id}/password`, {
      method: 'POST',
      body: JSON.stringify({ password }),
    }),

  // User groups (DB-backed; may be linked to an OCI tenant).
  getUserGroups: () => request('/user-groups'),
  getUserGroup: (id) => request(`/user-groups/${id}`),
  createUserGroup: (payload) =>
    request('/user-groups', { method: 'POST', body: JSON.stringify(payload) }),
  updateUserGroup: (id, payload) =>
    request(`/user-groups/${id}`, { method: 'PATCH', body: JSON.stringify(payload) }),
  deleteUserGroup: (id) => request(`/user-groups/${id}`, { method: 'DELETE' }),
  createGroupContract: (groupId, payload) =>
    request(`/user-groups/${groupId}/contracts`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  // Stored cost & billing. getUsageRecords is the client-facing read (scoped to
  // the caller's groups, served from the database); syncUsage is the superadmin
  // pull that refreshes it from the provider.
  // Returns the whole envelope: the rows plus meta (totals, lastSyncedAt).
  getUsageRecords: (params = {}) => {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== '' && v != null)
    ).toString();
    return requestEnvelope(`/usage-records${qs ? `?${qs}` : ''}`);
  },
  syncUsage: (payload) =>
    request('/usage-records/sync', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  // The monthly snapshot behind a group's own spend chart. getUsageMonths is the
  // client-facing read — billing per service per month, scoped to the caller's
  // groups, with no cost field in the shape at all — and returns the envelope, whose
  // meta.axis is every month from the earliest stored to the latest (a month with
  // nothing reported stays a gap). syncUsageMonths is the superadmin pull that
  // refreshes it; { providerId, groupId, months }.
  getUsageMonths: (params = {}) => {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== '' && v != null)
    ).toString();
    return requestEnvelope(`/usage-months${qs ? `?${qs}` : ''}`);
  },
  syncUsageMonths: (payload) =>
    request('/usage-months/sync', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  // The same client-facing chart per day, over a window: { groupId, providerId,
  // start, end }. Read from the database like getUsageMonths — never from the
  // provider — but aggregated from the *daily* stored rows (the ones syncUsage
  // writes) rather than the monthly snapshot, so it only ever reaches as far back as
  // a superadmin has stored days for. Rows are ({ date, service, currency, billing })
  // with no cost field in the shape at all.
  //
  // meta.axis is therefore the earliest→latest day *stored* inside the window, not
  // the window itself: a day nobody synced was never asked about, and drawing it
  // would report no spend for a day no one looked at.
  getUsageDays: (params = {}) => {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== '' && v != null)
    ).toString();
    return requestEnvelope(`/usage-days${qs ? `?${qs}` : ''}`);
  },

  // Price management: per-user-group unit price overrides, keyed by SKU.
  getPrices: (groupId) =>
    request(`/prices${groupId ? `?groupId=${encodeURIComponent(groupId)}` : ''}`),
  getGroupPrices: (groupId) => request(`/user-groups/${groupId}/prices`),
  createGroupPrice: (groupId, payload) =>
    request(`/user-groups/${groupId}/prices`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  // Copies the provider's published price list into a group's prices in one
  // call (superadmin). Resolves to { catalogue, copied, updated, skipped,
  // tiered }. What it writes is ordinary overrides — the whole published ladder
  // per SKU, editable band by band, and a later catalogue sync does not move
  // them.
  copyGroupPrices: (groupId, payload) =>
    request(`/user-groups/${groupId}/prices/copy`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  // Copies this group's own manually-set prices into one or more other groups
  // (superadmin). Payload: { targetGroupIds, overwrite }. Resolves to a batch:
  // { source, groups, copied, updated, skipped, results, failures } — the same
  // shape as copyAllGroupPrices, but the source is another group rather than
  // the provider's published list.
  copyGroupPricesToGroups: (groupId, payload) =>
    request(`/user-groups/${groupId}/prices/copy-to-groups`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  // The same copy applied to every user group in one call (superadmin). Resolves
  // to a batch: the shared { catalogue, currency }, the summed counters, a
  // `results` entry per group that was priced, and `failures` for any that were
  // not — one group failing does not stop the rest, so both lists can be
  // non-empty at once. Its own method rather than a flag because the response is
  // a different shape, and a caller should not have to re-read its own request to
  // know what it got back.
  copyAllGroupPrices: (payload) =>
    request('/user-groups/prices/copy', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  updatePrice: (id, payload) =>
    request(`/prices/${id}`, { method: 'PATCH', body: JSON.stringify(payload) }),
  deletePrice: (id) => request(`/prices/${id}`, { method: 'DELETE' }),

  // Policies: a named grant binding a user group to a list of services.
  getPolicyOptions: () => request('/policy-options'),
  getPolicies: () => request('/policies'),
  getPolicy: (id) => request(`/policies/${id}`),
  createPolicy: (payload) =>
    request('/policies', { method: 'POST', body: JSON.stringify(payload) }),
  updatePolicy: (id, payload) =>
    request(`/policies/${id}`, { method: 'PATCH', body: JSON.stringify(payload) }),
  deletePolicy: (id) => request(`/policies/${id}`, { method: 'DELETE' }),

  // Scheduled background jobs (superadmin). There is no create or delete: a job
  // exists because the backend defines one, and these four calls only change
  // whether and when it runs.
  //
  // start arms it, stop disarms it and aborts a sweep in flight, restart does both
  // (abandoning the run and re-arming the timer from now), and run triggers it
  // immediately without touching the schedule. Each resolves to the job's new
  // state — but `run` returns as soon as the sweep has *started*, so its running
  // flag is what comes back, not its result: re-read getJobs for that.
  getJobs: () => request('/jobs'),
  startJob: (id) => request(`/jobs/${id}/start`, { method: 'POST' }),
  stopJob: (id) => request(`/jobs/${id}/stop`, { method: 'POST' }),
  restartJob: (id) => request(`/jobs/${id}/restart`, { method: 'POST' }),
  runJob: (id) => request(`/jobs/${id}/run`, { method: 'POST' }),

  getContracts: () => request('/contracts'),
  getContract: (id) => request(`/contracts/${id}`),
  createContract: (payload) =>
    request('/contracts', { method: 'POST', body: JSON.stringify(payload) }),
  updateContract: (id, payload) =>
    request(`/contracts/${id}`, { method: 'PATCH', body: JSON.stringify(payload) }),
  deleteContract: (id) => request(`/contracts/${id}`, { method: 'DELETE' }),

  // Provider capabilities (superadmin). Define what management features each
  // provider supports and whether the provider is enabled.
  getProviderCapabilities: () => request('/provider-capabilities'),
  getProviderCapability: (id) => request(`/provider-capabilities/${id}`),
  updateProviderCapability: (id, payload) =>
    request(`/provider-capabilities/${id}`, { method: 'PATCH', body: JSON.stringify(payload) }),

  // QEMU servers (superadmin). Register and manage KVM/QEMU hypervisor servers per region.
  getQemuServers: (region = '') =>
    request(`/qemu-servers${region ? `?region=${encodeURIComponent(region)}` : ''}`),
  getQemuServer: (id) => request(`/qemu-servers/${id}`),
  createQemuServer: (payload) =>
    request('/qemu-servers', { method: 'POST', body: JSON.stringify(payload) }),
  updateQemuServer: (id, payload) =>
    request(`/qemu-servers/${id}`, { method: 'PATCH', body: JSON.stringify(payload) }),
  deleteQemuServer: (id) => request(`/qemu-servers/${id}`, { method: 'DELETE' }),
  checkQemuServer: (id) => request(`/qemu-servers/${id}/check`, { method: 'POST' }),
  syncQemuServers: () => request('/qemu-servers/sync', { method: 'POST' }),
  syncQemuServerResources: (id) => request(`/qemu-servers/${id}/sync`, { method: 'POST' }),
  computeQemuServerResources: (id) => request(`/qemu-servers/${id}/compute`, { method: 'POST' }),
};
